// Pure helpers for the Arkusz AI grid — formatting, freshness, footer aggregates.
import type { AiCellValue, AiColumnFmt, AiSheetCell } from "./types";

export const LEVEL_LABEL: Record<string, string> = {
  low: "niskie",
  mid: "średnie",
  high: "wysokie",
};

export const fmtMoneyPL = (v: number): string =>
  new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 0 }).format(v) + " zł";

export const fmtDatePL = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${d}.${m}.${y}` : iso;
};

/** Main display string for a cell value, by column format. */
export function fmtCellValue(fmt: AiColumnFmt, v: AiCellValue | null): string {
  if (!v || v.value === null || v.value === undefined || v.value === "") return "—";
  const raw = v.value;
  if (fmt === "money" && typeof raw === "number") return fmtMoneyPL(raw);
  if (fmt === "percent" && typeof raw === "number") return `${Math.round(raw * 10) / 10}%`;
  if (fmt === "number" && typeof raw === "number") {
    const s = new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 2 }).format(raw);
    return v.unit ? `${s} ${v.unit}` : s;
  }
  if (fmt === "date" && typeof raw === "string") return fmtDatePL(raw);
  if (fmt === "level") return LEVEL_LABEL[String(v.level ?? raw)] ?? String(raw);
  return String(raw);
}

/** Days from `now` to an ISO date (negative = past). */
export const daysUntil = (iso: string, now: Date = new Date()): number =>
  Math.round((new Date(iso.slice(0, 10)).getTime() - now.getTime()) / 864e5);

export type DeadlineTone = "red" | "amber" | "green";
export const deadlineTone = (days: number): DeadlineTone =>
  days < 60 ? "red" : days < 120 ? "amber" : "green";

/** A result older than `maxDays` gets the amber dot. */
export function isStale(computedAt: string | null, maxDays = 5, now: Date = new Date()): boolean {
  if (!computedAt) return false;
  return now.getTime() - new Date(computedAt).getTime() > maxDays * 864e5;
}

export interface FooterAggregate {
  text: string;
  /** true → some visible cells were excluded (no_access) — render the asterisk. */
  partial: boolean;
}

export type AggKind = "sum" | "avg" | "median" | "min" | "max" | "count" | "filled" | "none";

export const AGG_LABEL: Record<AggKind, string> = {
  sum: "suma",
  avg: "średnia",
  median: "mediana",
  min: "min",
  max: "max",
  count: "liczba wierszy",
  filled: "wypełnione",
  none: "—",
};

/** Which aggregates make sense for a column format. */
export function aggOptions(fmt: AiColumnFmt): AggKind[] {
  if (["number", "money", "percent"].includes(fmt)) {
    return ["sum", "avg", "median", "min", "max", "filled", "count", "none"];
  }
  if (fmt === "date") return ["min", "max", "filled", "count", "none"];
  return ["filled", "count", "none"];
}

export const defaultAgg = (fmt: AiColumnFmt): AggKind =>
  fmt === "money" || fmt === "number" ? "sum"
    : fmt === "percent" ? "avg"
      : "none";

const median = (xs: number[]): number => {
  const a = [...xs].sort((p, q) => p - q);
  const m = a.length >> 1;
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};

const fmtNum = (v: number, fmt: AiColumnFmt): string =>
  fmt === "money" ? fmtMoneyPL(v)
    : fmt === "percent" ? `${Math.round(v * 10) / 10}%`
      : new Intl.NumberFormat("pl-PL", { maximumFractionDigits: 2 }).format(v);

/**
 * Footer for one column over the VISIBLE cells, with the aggregate the user picked.
 * Cells without access are excluded and flagged — a sum that silently swallowed
 * "brak dostępu" would lie.
 */
export function computeAgg(
  kind: AggKind,
  fmt: AiColumnFmt,
  cells: (AiSheetCell | undefined)[],
): FooterAggregate | null {
  if (kind === "none") return null;
  let partial = false;
  const nums: number[] = [];
  const dates: string[] = [];
  let filled = 0;
  for (const c of cells) {
    if (!c) continue;
    if (c.status === "no_access") {
      partial = true;
      continue;
    }
    const v = c.value?.value;
    if (v === null || v === undefined || v === "") continue;
    filled += 1;
    if (typeof v === "number") nums.push(v);
    else if (fmt === "date" && typeof v === "string") dates.push(v.slice(0, 10));
  }
  if (kind === "count") return { text: String(cells.length), partial: false };
  if (kind === "filled") return { text: `${filled}/${cells.length}`, partial };
  if (fmt === "date") {
    if (!dates.length) return { text: "—", partial };
    const d = kind === "min" ? dates.reduce((a, b) => (a < b ? a : b))
      : dates.reduce((a, b) => (a > b ? a : b));
    return { text: fmtDatePL(d), partial };
  }
  if (!nums.length) return { text: "—", partial };
  const v = kind === "sum" ? nums.reduce((a, b) => a + b, 0)
    : kind === "avg" ? nums.reduce((a, b) => a + b, 0) / nums.length
      : kind === "median" ? median(nums)
        : kind === "min" ? Math.min(...nums)
          : Math.max(...nums);
  const prefix = kind === "avg" ? "śr. " : kind === "median" ? "med. " : "";
  return { text: prefix + fmtNum(v, fmt), partial };
}

/** Back-compat wrapper: the format's default aggregate. */
export function footerAggregate(
  fmt: AiColumnFmt,
  cells: (AiSheetCell | undefined)[],
): FooterAggregate | null {
  return computeAgg(defaultAgg(fmt), fmt, cells);
}

export const aggStorageKey = (sheetId: string): string => `pp-ai-sheet-aggs:${sheetId}`;

export function loadAggs(sheetId: string): Record<string, AggKind> {
  try {
    const raw = localStorage.getItem(aggStorageKey(sheetId));
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter(([, v]) => typeof v === "string" && v in AGG_LABEL),
    ) as Record<string, AggKind>;
  } catch {
    return {};
  }
}

export function saveAggs(sheetId: string, aggs: Record<string, AggKind>): void {
  try {
    localStorage.setItem(aggStorageKey(sheetId), JSON.stringify(aggs));
  } catch {
    /* private window / quota — a footer preference is never worth an error */
  }
}

/** Compact "było: X" text for the previous value. */
export function previousLabel(fmt: AiColumnFmt, prev: AiCellValue | null): string | null {
  if (!prev || prev.value === null || prev.value === undefined) return null;
  return fmtCellValue(fmt, prev);
}

/* ── client-side sort & per-column filters ─────────────────────────────────── */

export interface ColFilter {
  min?: string;
  max?: string;
  text?: string;
  pick?: string;
}

export const filterActive = (f: ColFilter | undefined): boolean =>
  !!f && !!(f.min?.trim() || f.max?.trim() || f.text?.trim() || f.pick);

/** Does a column value pass the filter? Empty values fail any active filter. */
export function matchColFilter(
  f: ColFilter,
  fmt: AiColumnFmt,
  v: string | number | null,
): boolean {
  if (v === null || v === undefined || v === "") return false;
  if (["number", "money", "percent"].includes(fmt)) {
    const n = Number(v);
    if (Number.isNaN(n)) return false;
    if (f.min?.trim() && n < Number(f.min)) return false;
    if (f.max?.trim() && n > Number(f.max)) return false;
    return true;
  }
  if (fmt === "level" || fmt === "flag") {
    return !f.pick || String(v) === f.pick;
  }
  return !f.text?.trim() || String(v).toLowerCase().includes(f.text.toLowerCase());
}

const LEVEL_ORDER: Record<string, number> = { low: 0, mid: 1, high: 2 };

/** Sort comparator: numbers numerically, levels by severity, strings pl-aware;
 * empty always LAST regardless of direction. */
export function cmpVals(a: string | number | null, b: string | number | null): number {
  const emptyA = a === null || a === undefined || a === "";
  const emptyB = b === null || b === undefined || b === "";
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  const la = LEVEL_ORDER[String(a)];
  const lb = LEVEL_ORDER[String(b)];
  if (la !== undefined && lb !== undefined) return la - lb;
  return String(a).localeCompare(String(b), "pl");
}


/* ── column widths (per viewer, remembered per sheet) ─────────────────────── */

/** Deliberately tight: a director looks at many columns at once, and every pixel
 * of an unused column is a column that fell off the screen. Widened by dragging. */
export const DEFAULT_COL_WIDTH: Record<AiColumnFmt, number> = {
  money: 118,
  number: 88,
  percent: 84,
  date: 104,
  flag: 104,
  level: 150,
  text: 168,
};

// Low on purpose: at this width the header and cells wrap instead of blocking the
// drag, so a column can be squeezed to a sliver when the neighbour matters more.
export const MIN_COL_WIDTH = 36;
export const MAX_COL_WIDTH = 640;

export const clampWidth = (px: number): number =>
  Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, Math.round(px)));

export const widthStorageKey = (sheetId: string): string => `pp-ai-sheet-widths:${sheetId}`;

export function loadWidths(sheetId: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(widthStorageKey(sheetId));
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter(([, v]) => typeof v === "number" && Number.isFinite(v))
        .map(([k, v]) => [k, clampWidth(v as number)]),
    );
  } catch {
    return {};
  }
}

export function saveWidths(sheetId: string, widths: Record<string, number>): void {
  try {
    localStorage.setItem(widthStorageKey(sheetId), JSON.stringify(widths));
  } catch {
    /* private window / quota — widths are a convenience, never a blocker */
  }
}


/* ── hidden rows/columns (Excel-style, per viewer) ────────────────────────── */

export const hiddenStorageKey = (sheetId: string): string => `pp-ai-sheet-hidden:${sheetId}`;

export interface HiddenState {
  cols: string[];
  rows: string[];
}

export function loadHidden(sheetId: string): HiddenState {
  try {
    const raw = localStorage.getItem(hiddenStorageKey(sheetId));
    const parsed = raw ? JSON.parse(raw) : null;
    const arr = (v: unknown) => (Array.isArray(v) ? v.filter((x) => typeof x === "string") : []);
    return { cols: arr(parsed?.cols), rows: arr(parsed?.rows) };
  } catch {
    return { cols: [], rows: [] };
  }
}

export function saveHidden(sheetId: string, state: HiddenState): void {
  try {
    localStorage.setItem(hiddenStorageKey(sheetId), JSON.stringify(state));
  } catch {
    /* hiding is a view preference — never worth an error */
  }
}
